import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { memberTabs } from 'src/modules/member/models/memberTabs';
import { getAllProfileGroups, getProfileGroupByIdAllItems } from 'src/shared/services/profileGroupService';
import { WidgetInstance } from '../types';
import { RealMembersByProfileConfig } from './realMembersByProfileWidget';

type ProfileItemOption = {
  id: number;
  name: string;
  groupTitle: string;
};

type Props = {
  open: boolean;
  instance: WidgetInstance | null;
  onClose: () => void;
  onSave: (instance: WidgetInstance) => void;
};

const STATUS_OPTIONS = memberTabs.filter((t) => t.key !== 'active');

export const RealMembersByProfileConfigDialog = ({ open, instance, onClose, onSave }: Props) => {
  const { t } = useTranslation(['member']);
  const isMounted = useIsMounted();
  const initial = (instance?.config as RealMembersByProfileConfig | undefined) ?? { statusKeys: ['paid', 'notPaid'], profileItemIds: [] };

  const [statusKeys, setStatusKeys] = useState<string[]>(initial.statusKeys);
  const [selectedItems, setSelectedItems] = useState<ProfileItemOption[]>([]);
  const [profileGroups, setProfileGroups] = useState<ProfileGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStatusKeys(initial.statusKeys);
    setLoadingGroups(true);
    // getAllProfileGroups returnerar bara grupp-metadata utan items —
    // hämta items per grupp parallellt så vi får hela trädet.
    getAllProfileGroups(true, true)
      .then(async (res) => {
        const groups = res.data.results;
        const populated = await Promise.all(
          groups.map((g) =>
            getProfileGroupByIdAllItems(g.memberProfile_id, true)
              .then((r) => r.data)
              .catch(() => g),
          ),
        );
        if (!isMounted()) return;
        setProfileGroups(populated);
        const allItems = populated.flatMap((g) =>
          (g.profileItems ?? []).map((i) => ({ id: i.memberProfileItem_id, name: i.name, groupTitle: g.title })),
        );
        setSelectedItems(allItems.filter((opt) => initial.profileItemIds.includes(opt.id)));
      })
      .catch((e) => ConsoleHelper.error(e))
      .finally(() => {
        if (isMounted()) setLoadingGroups(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const allOptions = useMemo<ProfileItemOption[]>(
    () => profileGroups.flatMap((g) => (g.profileItems ?? []).map((i) => ({ id: i.memberProfileItem_id, name: i.name, groupTitle: g.title }))),
    [profileGroups],
  );

  const toggleStatus = (key: string) => {
    setStatusKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleSave = () => {
    if (!instance) return;
    onSave({
      ...instance,
      config: {
        statusKeys,
        profileItemIds: selectedItems.map((i) => i.id),
        profileItemLabels: selectedItems.map((i) => i.name).join(', '),
      } as RealMembersByProfileConfig,
    });
    onClose();
  };

  if (!instance) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Konfigurera: Medlemmar per profil</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={1}>
            <FormLabel sx={{ fontSize: 12 }}>Status (en eller flera)</FormLabel>
            <FormGroup row>
              {STATUS_OPTIONS.map((s) => (
                <FormControlLabel
                  key={s.key}
                  control={<Checkbox size='small' checked={statusKeys.includes(s.key)} onChange={() => toggleStatus(s.key)} />}
                  label={t(s.label)}
                />
              ))}
            </FormGroup>
          </Stack>
          <Stack spacing={1}>
            <FormLabel sx={{ fontSize: 12 }}>Profil-items (en eller flera)</FormLabel>
            <Autocomplete
              multiple
              loading={loadingGroups}
              options={allOptions}
              groupBy={(option) => option.groupTitle}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedItems}
              onChange={(_, value) => setSelectedItems(value)}
              renderInput={(params) => <TextField {...params} placeholder='Sök profilgrupp eller -värde…' size='small' />}
              sx={{ '& .MuiAutocomplete-tag': { maxWidth: '70%' } }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant='contained' onClick={handleSave}>
          Spara
        </Button>
      </DialogActions>
    </Dialog>
  );
};
